import { Box, Divider, Typography, typographyClasses, type BoxProps } from '@mui/material';
import { merge } from 'lodash';
import type { PropsWithChildren, ReactNode } from 'react';

export interface AoaSectionHeaderProps extends Omit<BoxProps, 'title'> {
  /**
   * Data-qa tag for E2E test purposes
   */
  readonly 'data-qa'?: string;
  /**
   * The content to show as subtitle, lighter grey and below the title
   */
  readonly subtitle?: ReactNode;
  /**
   * The title to show in the first Typography component
   */
  readonly title: ReactNode;
}

/**
 * Constructs a section header using pre-defined Rijks styling
 *
 * @param props - Props to pass to the section header
 * @example
 * ```jsx
 * <AoaSectionHeader title='This is a title' subtitle='And this is a subtitle' />
 * ```
 */
export function AoaSectionHeader({ title, subtitle = '', children, ...props }: PropsWithChildren<AoaSectionHeaderProps>) {
  return (
    <Box
      {...props}
      sx={merge(
        {
          marginRight: 0,
          marginBottom: 2,
          'breakpoints.down("sm")': {
            flexBasis: '100%',
            width: '100%',
            marginRight: 0,

            '&:not(:first-of-type)': {
              marginTop: 3
            }
          },
          [`.${typographyClasses.root}`]: {
            fontFamily: 'var(--font-text)'
          }
        },
        props.sx
      )}
    >
      <Typography color='textPrimary' component='h1' data-qa='section-title' sx={{ fontSize: 'var(--font-size-h1)' }} variant='h5'>
        {title}
      </Typography>
      {Boolean(subtitle) && (
        <Typography color='textSecondary' component='span' data-qa='section-subtitle' sx={{ fontSize: 'var(--font-size-text)' }} variant='subtitle1'>
          {subtitle}
        </Typography>
      )}
      <Divider
        sx={{
          backgroundColor: 'var(--color-rijks-skyblue)',
          marginBottom: 3,
          paddingBottom: 0.25
        }}
      />
      {children}
    </Box>
  );
}
